import React, { useState, useMemo, useCallback } from 'react';
import { 
  ChevronLeft, User, Plus, Edit2, X, Search, Filter, ChevronDown, ChevronUp, 
  Mail, Phone, MapPin, Building, MoreVertical, Calendar
} from 'lucide-react';
import toast from 'react-hot-toast';
import ContactEditModal from '../common/ContactEditModal';
import { AVAILABLE_TAGS, getTagColor, getTagLabel } from '../../utils/contactTags';
import { AddContactGate } from '../subscription/FeatureGates';

// Move outside component to prevent re-renders
const SortHeader = ({ field, children, sortField, sortDir, onSort, className = '' }) => (
  <th 
    onClick={() => onSort(field)}
    className={`px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-gray-50 dark:bg-gray-900 cursor-pointer select-none hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors ${className}`}
  >
    <div className="flex items-center gap-1">
      {children}
      {sortField === field && (sortDir === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
    </div>
  </th>
);

// Contact Card for Mobile
const ContactCard = ({ contact, onEdit, onDelete, getTagColor, getTagLabel }) => {
  const [showActions, setShowActions] = useState(false);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 relative">
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg">
            {contact.name?.charAt(0)?.toUpperCase() || '?'}
          </div>
          <div>
            <h3 className="font-semibold text-gray-800 dark:text-gray-100">{contact.name}</h3>
            <span className={`text-xs px-2 py-0.5 rounded-full ${
              contact.fromShow 
                ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400' 
                : 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400'
            }`}>
              {contact.fromShow ? 'From Show' : 'Manual'}
            </span>
          </div>
        </div>
        
        {/* Actions Menu */}
        <div className="relative">
          <button 
            onClick={() => setShowActions(!showActions)}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <MoreVertical size={18} />
          </button>
          {showActions && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowActions(false)} />
              <div className="absolute right-0 top-full mt-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-20 min-w-[120px]">
                <button 
                  onClick={() => { onEdit(contact); setShowActions(false); }}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                >
                  <Edit2 size={14} /> Edit
                </button>
                <button 
                  onClick={() => { onDelete(contact); setShowActions(false); }}
                  className="w-full px-4 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                >
                  <X size={14} /> Delete
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Tags */}
      {contact.tags?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {contact.tags.map(tag => (
            <span key={tag} className={`text-xs px-2.5 py-1 rounded-full font-medium ${getTagColor(tag)}`}>
              {getTagLabel(tag)}
            </span>
          ))}
        </div>
      )}

      {/* Contact Info */}
      <div className="space-y-2">
        {contact.email && (
          <a href={`mailto:${contact.email}`} className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400">
            <Mail size={14} className="text-gray-400" />
            <span className="truncate">{contact.email}</span>
          </a>
        )}
        {contact.phone && (
          <a href={`tel:${contact.phone}`} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <Phone size={14} className="text-gray-400" />
            {contact.phone}
          </a>
        )}
        {contact.venue && (
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
            <Building size={14} className="text-gray-400" />
            {contact.venue}
          </div>
        )}
        {contact.city && (
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
            <MapPin size={14} className="text-gray-400" />
            {contact.city}
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="flex gap-2 mt-4 pt-3 border-t border-gray-100 dark:border-gray-700">
        {contact.email && (
          <a 
            href={`mailto:${contact.email}`}
            className="flex-1 py-2 text-center text-sm font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
          >
            Email
          </a>
        )}
        {contact.phone && (
          <a 
            href={`tel:${contact.phone}`}
            className="flex-1 py-2 text-center text-sm font-medium text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors"
          >
            Call
          </a>
        )}
        {!contact.email && !contact.phone && (
          <button 
            onClick={() => onEdit(contact)}
            className="flex-1 py-2 text-center text-sm font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            Add Info
          </button>
        )}
      </div>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ label, value, color, icon: Icon }) => (
  <div className={`${color} p-3 md:p-4 rounded-xl`}>
    <div className="flex items-center justify-between">
      <div>
        <p className="text-xs text-gray-600 dark:text-gray-400">{label}</p>
        <p className="text-xl md:text-2xl font-bold">{value}</p>
      </div>
      {Icon && <Icon size={24} className="opacity-30" />}
    </div>
  </div>
);

const Contacts = ({ shows, contacts, onNavigate, onAddContact, onUpdateContact, onDeleteContact, onUpdateShow, loading }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);
  const [sortField, setSortField] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', venue: '', city: '', notes: '', tags: [] });
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);

  // Memoized contact aggregation
  const allContacts = useMemo(() => {
    const result = [];
    if (Array.isArray(contacts)) {
      contacts.forEach(c => result.push({ ...c, fromShow: false, tags: c.tags || [] }));
    }
    if (shows && typeof shows === 'object') {
      Object.entries(shows).forEach(([dateKey, showsArray]) => {
        if (Array.isArray(showsArray)) {
          showsArray.forEach(show => {
            if (show.contactName || show.contactEmail || show.contactPhone) {
              result.push({
                id: `show-${show.id}`, name: show.contactName || 'Unknown', email: show.contactEmail || '',
                phone: show.contactPhone || '', venue: show.venue || '', city: show.city || '',
                dateKey, showId: show.id, fromShow: true, tags: show.contactTags || []
              });
            }
          });
        }
      });
    }
    return result;
  }, [shows, contacts]);

  const uniqueContacts = useMemo(() => {
    const seen = new Map();
    return allContacts.filter((c, i) => {
      const key = (c.email || c.phone || c.name || `index-${i}`).toLowerCase();
      if (seen.has(key)) return false;
      seen.set(key, true);
      return true;
    });
  }, [allContacts]);

  const tagCounts = useMemo(() => 
    Object.fromEntries(AVAILABLE_TAGS.map(t => [t.value, uniqueContacts.filter(c => c.tags?.includes(t.value)).length]))
  , [uniqueContacts]);

  const sortedContacts = useMemo(() => {
    let filtered = uniqueContacts;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(c => [c.name, c.email, c.phone, c.venue].some(f => f?.toLowerCase().includes(q)));
    }
    if (selectedTags.length) {
      filtered = filtered.filter(c => c.tags && selectedTags.some(t => c.tags.includes(t)));
    }
    return filtered.sort((a, b) => {
      const aVal = (a[sortField] || '').toLowerCase();
      const bVal = (b[sortField] || '').toLowerCase();
      return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    });
  }, [uniqueContacts, searchQuery, selectedTags, sortField, sortDir]);

  // Callbacks
  const toggleTag = useCallback((tag) => setSelectedTags(p => p.includes(tag) ? p.filter(t => t !== tag) : [...p, tag]), []);
  const toggleFormTag = useCallback((tag) => setFormData(p => ({ ...p, tags: p.tags.includes(tag) ? p.tags.filter(t => t !== tag) : [...p.tags, tag] })), []);
  const clearFilters = useCallback(() => { setSelectedTags([]); setSearchQuery(''); }, []);

  const handleSort = useCallback((field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  }, [sortField]);

  const handleAddContact = useCallback(() => {
    if (!formData.name.trim()) return toast.error('Name is required');
    onAddContact(formData);
    toast.success('Contact added');
    setFormData({ name: '', email: '', phone: '', venue: '', city: '', notes: '', tags: [] });
    setShowForm(false);
  }, [formData, onAddContact]);

  const handleEditContact = useCallback((contact) => {
    setEditingContact(contact);
    setEditModalOpen(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setEditModalOpen(false);
    setEditingContact(null);
  }, []);

  const handleModalUpdate = useCallback((contactId, updatedData) => {
    const contact = uniqueContacts.find(c => c.id === contactId);
    
    if (contact?.fromShow) {
      onUpdateShow(contact.dateKey, contact.showId, {
        contactName: updatedData.name,
        contactEmail: updatedData.email,
        contactPhone: updatedData.phone,
        contactTags: updatedData.tags || []
      });
      toast.success('Show contact updated');
    } else {
      onUpdateContact(contactId, updatedData);
      toast.success('Contact updated');
    }
    handleModalClose();
  }, [uniqueContacts, onUpdateShow, onUpdateContact, handleModalClose]);

  const handleDeleteContact = useCallback((contact) => {
    if (contact.fromShow) {
      if (window.confirm('Remove contact info from this show?')) {
        onUpdateShow(contact.dateKey, contact.showId, {
          contactName: '', contactEmail: '', contactPhone: '', contactTags: []
        });
        toast.success('Contact removed from show');
      }
      return;
    }
    if (window.confirm(`Delete ${contact.name}?`)) {
      onDeleteContact(contact.id);
      toast.success('Contact deleted');
    }
  }, [onUpdateShow, onDeleteContact]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
        <div className="animate-pulse text-xl text-gray-600 dark:text-gray-300">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 dark:from-gray-900 dark:to-gray-800">
      {/* Mobile Header */}
      <div className="md:hidden sticky top-0 z-30 bg-white/80 dark:bg-gray-800/80 backdrop-blur-lg border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between p-4">
          <button onClick={() => onNavigate('dashboard')} className="p-2 -ml-2 text-gray-600 dark:text-gray-300">
            <ChevronLeft size={24} />
          </button>
          <h1 className="text-lg font-bold text-gray-800 dark:text-gray-100">Contacts</h1>
          <AddContactGate currentCount={uniqueContacts.length}>
  <button
    onClick={() => setShowForm(!showForm)}
    className="p-2 -mr-2 text-blue-600 dark:text-blue-400"
  >
    <Plus size={24} />
  </button>
</AddContactGate>
        </div>
        
        {/* Mobile Search */}
        <div className="px-4 pb-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search contacts..."
              className="w-full pl-10 pr-4 py-2.5 bg-gray-100 dark:bg-gray-700 border-0 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-gray-100"
            />
          </div>
        </div>

        {/* Mobile Filter Toggle */}
        <div className="px-4 pb-3 flex gap-2 overflow-x-auto scrollbar-hide">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              showFilters || selectedTags.length > 0
                ? 'bg-purple-600 text-white'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
            }`}
          >
            <Filter size={14} />
            Filters {selectedTags.length > 0 && `(${selectedTags.length})`}
          </button>
          {selectedTags.map(tag => (
            <button
              key={tag}
              onClick={() => toggleTag(tag)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-sm font-medium ${getTagColor(tag)} flex items-center gap-1`}
            >
              {getTagLabel(tag)}
              <X size={12} />
            </button>
          ))}
        </div>
      </div>

      <div className="p-4 md:p-8 max-w-6xl mx-auto">
        {/* Desktop Header */}
        <div className="hidden md:block bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden mb-6">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100">Contacts</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {sortedContacts.length} of {uniqueContacts.length} contacts
                </p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => onNavigate('dashboard')} className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors">
                  Dashboard
                </button>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    showFilters || selectedTags.length > 0
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  <Filter size={18} /> Filters {selectedTags.length > 0 && `(${selectedTags.length})`}
                </button>
                <AddContactGate currentCount={uniqueContacts.length}>
  <button
    onClick={() => setShowForm(!showForm)}
    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
  >
    <Plus size={18} /> Add Contact
  </button>
</AddContactGate>
              </div>
            </div>

            {/* Stats Row */}
            <div className="grid grid-cols-4 gap-4 mt-6">
              <StatCard label="Total" value={uniqueContacts.length} color="bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400" icon={User} />
              <StatCard label="From Shows" value={uniqueContacts.filter(c => c.fromShow).length} color="bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400" icon={Calendar} />
              <StatCard label="Manual" value={uniqueContacts.filter(c => !c.fromShow).length} color="bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400" icon={User} />
              <StatCard label="With Email" value={uniqueContacts.filter(c => c.email).length} color="bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400" icon={Mail} />
            </div>
          </div>

          {/* Desktop Filters */}
          {showFilters && (
            <div className="p-4 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-gray-800 dark:text-gray-100">Filter by Tags</h3>
                {selectedTags.length > 0 && (
                  <button onClick={clearFilters} className="text-sm text-blue-600 dark:text-blue-400 hover:underline">
                    Clear all
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {AVAILABLE_TAGS.map(tag => (
                  <button
                    key={tag.value}
                    onClick={() => toggleTag(tag.value)}
                    className={`px-3 py-1.5 rounded-full text-sm font-semibold transition-all ${
                      selectedTags.includes(tag.value)
                        ? tag.color + ' ring-2 ring-offset-2 ring-blue-500 dark:ring-offset-gray-900'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-300 dark:hover:bg-gray-600'
                    }`}
                  >
                    {tag.label} ({tagCounts[tag.value] || 0})
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Desktop Search */}
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search contacts..."
                className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-xl focus:outline-none focus:border-blue-500 transition-colors"
              />
            </div>
          </div>

          {/* Desktop Table */}
          {sortedContacts.length === 0 ? (
            <div className="text-center py-16 px-4">
              <User size={48} className="mx-auto mb-4 text-gray-400" />
              <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-2">
                {searchQuery || selectedTags.length ? 'No contacts found' : 'No Contacts Yet'}
              </h3>
              <p className="text-gray-500 dark:text-gray-400 mb-6">
                {searchQuery || selectedTags.length ? 'Try adjusting your search or filters.' : 'Add your first contact to get started.'}
              </p>
              {searchQuery || selectedTags.length ? (
  <button
    onClick={clearFilters}
    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
  >
    Clear Filters
  </button>
) : (
  <AddContactGate currentCount={uniqueContacts.length}>
    <button
      onClick={() => setShowForm(true)}
      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
    >
      Add Contact
    </button>
  </AddContactGate>
)}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr>
                    <SortHeader field="name" sortField={sortField} sortDir={sortDir} onSort={handleSort}>Name</SortHeader>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-gray-50 dark:bg-gray-900">Tags</th>
                    <SortHeader field="email" sortField={sortField} sortDir={sortDir} onSort={handleSort}>Email</SortHeader>
                    <SortHeader field="phone" sortField={sortField} sortDir={sortDir} onSort={handleSort}>Phone</SortHeader>
                    <SortHeader field="venue" sortField={sortField} sortDir={sortDir} onSort={handleSort}>Venue</SortHeader>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-gray-50 dark:bg-gray-900">Source</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-gray-50 dark:bg-gray-900">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {sortedContacts.map((contact, idx) => (
                    <tr key={contact.id} className={`${idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-900/50'} hover:bg-blue-50 dark:hover:bg-gray-700/50 transition-colors`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm">
                            {contact.name?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                          <span className="font-semibold text-gray-800 dark:text-gray-200">{contact.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {contact.tags?.slice(0, 2).map(tag => (
                            <span key={tag} className={`text-xs px-2 py-0.5 rounded-full ${getTagColor(tag)}`}>
                              {getTagLabel(tag)}
                            </span>
                          ))}
                          {contact.tags?.length > 2 && (
                            <span className="text-xs text-gray-500 dark:text-gray-400">+{contact.tags.length - 2}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {contact.email ? (
                          <a href={`mailto:${contact.email}`} className="text-sm text-blue-600 dark:text-blue-400 hover:underline">
                            {contact.email}
                          </a>
                        ) : <span className="text-sm text-gray-400">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        {contact.phone ? (
                          <a href={`tel:${contact.phone}`} className="text-sm text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400">
                            {contact.phone}
                          </a>
                        ) : <span className="text-sm text-gray-400">—</span>}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                        {contact.venue || <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          contact.fromShow 
                            ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400' 
                            : 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400'
                        }`}>
                          {contact.fromShow ? 'Show' : 'Manual'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1 justify-end">
                          <button onClick={() => handleEditContact(contact)} className="p-2 text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-lg transition-colors" title="Edit">
                            <Edit2 size={16} />
                          </button>
                          <button onClick={() => handleDeleteContact(contact)} className="p-2 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors" title="Delete">
                            <X size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Mobile Filters Panel */}
        {showFilters && (
          <div className="md:hidden bg-white dark:bg-gray-800 rounded-xl shadow-lg p-4 mb-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-gray-800 dark:text-gray-100">Filter by Tags</h3>
              {selectedTags.length > 0 && (
                <button onClick={clearFilters} className="text-sm text-blue-600 dark:text-blue-400">
                  Clear
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {AVAILABLE_TAGS.map(tag => (
                <button
                  key={tag.value}
                  onClick={() => toggleTag(tag.value)}
                  className={`px-3 py-2 rounded-lg text-sm font-semibold transition-all ${
                    selectedTags.includes(tag.value)
                      ? tag.color + ' ring-2 ring-blue-500'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                  }`}
                >
                  {tag.label} ({tagCounts[tag.value] || 0})
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Mobile Add Form */}
        {showForm && (
          <div className="md:hidden bg-white dark:bg-gray-800 rounded-xl shadow-lg p-4 mb-4">
            <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-4">Add Contact</h2>
            <div className="space-y-3">
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Name *"
                className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-700 border-0 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-gray-100"
              />
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="Email"
                className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-700 border-0 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-gray-100"
              />
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="Phone"
                className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-700 border-0 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-gray-100"
              />
              <input
                type="text"
                value={formData.venue}
                onChange={(e) => setFormData({ ...formData, venue: e.target.value })}
                placeholder="Venue"
                className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-700 border-0 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-gray-100"
              />
              
              {/* Tags */}
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Tags</p>
                <div className="flex flex-wrap gap-2">
                  {AVAILABLE_TAGS.map(tag => (
                    <button
                      key={tag.value}
                      onClick={() => toggleFormTag(tag.value)}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                        formData.tags.includes(tag.value)
                          ? tag.color + ' ring-2 ring-blue-500'
                          : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-400'
                      }`}
                    >
                      {tag.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <button
                  onClick={handleAddContact}
                  className="flex-1 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors"
                >
                  Add Contact
                </button>
                <button
                  onClick={() => { setFormData({ name: '', email: '', phone: '', venue: '', city: '', notes: '', tags: [] }); setShowForm(false); }}
                  className="px-6 py-3 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-semibold"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Desktop Add Form */}
        {showForm && (
          <div className="hidden md:block bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden mb-6">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">Add New Contact</h2>
              <div className="grid grid-cols-4 gap-4 mb-4">
                <div>
                  <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Name *</label>
                  <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="John Doe"
                    className="w-full px-3 py-2 border-2 border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:border-blue-500 text-sm" />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Email</label>
                  <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="john@example.com"
                    className="w-full px-3 py-2 border-2 border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:border-blue-500 text-sm" />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Phone</label>
                  <input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="(555) 123-4567"
                    className="w-full px-3 py-2 border-2 border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:border-blue-500 text-sm" />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Venue</label>
                  <input type="text" value={formData.venue} onChange={(e) => setFormData({ ...formData, venue: e.target.value })} placeholder="The Blue Note"
                    className="w-full px-3 py-2 border-2 border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:border-blue-500 text-sm" />
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-2">Tags</label>
                <div className="flex flex-wrap gap-2">
                  {AVAILABLE_TAGS.map(tag => (
                    <button key={tag.value} onClick={() => toggleFormTag(tag.value)}
                      className={`px-3 py-1.5 rounded-full text-sm font-semibold transition-all ${
                        formData.tags.includes(tag.value) ? tag.color + ' ring-2 ring-blue-500' : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                      }`}>
                      {tag.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={handleAddContact} className="flex-1 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center justify-center gap-2">
                  <Plus size={18} /> Add Contact
                </button>
                <button onClick={() => { setFormData({ name: '', email: '', phone: '', venue: '', city: '', notes: '', tags: [] }); setShowForm(false); }}
                  className="px-6 py-2.5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Mobile Stats */}
        <div className="md:hidden grid grid-cols-2 gap-3 mb-4">
          <StatCard label="Total" value={uniqueContacts.length} color="bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400" />
          <StatCard label="From Shows" value={uniqueContacts.filter(c => c.fromShow).length} color="bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400" />
        </div>

        {/* Mobile Contact Cards */}
        <div className="md:hidden space-y-3">
          {sortedContacts.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-xl p-8 text-center">
              <User size={48} className="mx-auto mb-4 text-gray-300 dark:text-gray-600" />
              <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-2">
                {searchQuery || selectedTags.length ? 'No contacts found' : 'No Contacts Yet'}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                {searchQuery || selectedTags.length ? 'Try adjusting your filters.' : 'Add your first contact!'}
              </p>
              {searchQuery || selectedTags.length ? (
  <button
    onClick={clearFilters}
    className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium"
  >
    Clear Filters
  </button>
) : (
  <AddContactGate currentCount={uniqueContacts.length}>
    <button
      onClick={() => setShowForm(true)}
      className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium"
    >
      Add Contact
    </button>
  </AddContactGate>
)}
            </div>
          ) : (
            sortedContacts.map(contact => (
              <ContactCard
                key={contact.id}
                contact={contact}
                onEdit={handleEditContact}
                onDelete={handleDeleteContact}
                getTagColor={getTagColor}
                getTagLabel={getTagLabel}
              />
            ))
          )}
        </div>

        {/* Mobile Footer */}
        {sortedContacts.length > 0 && (
          <div className="md:hidden text-center text-sm text-gray-500 dark:text-gray-400 py-4">
            {sortedContacts.length} of {uniqueContacts.length} contacts
          </div>
        )}
      </div>

      {/* Contact Edit Modal */}
      <ContactEditModal
        contact={editingContact}
        isOpen={editModalOpen}
        onClose={handleModalClose}
        onUpdate={handleModalUpdate}
        availableTags={AVAILABLE_TAGS}
        getTagColor={getTagColor}
      />
    </div>
  );
};

export default Contacts;